import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../../services/api'
import { CrtButton } from '../../../components/crt'
import { useSceneStore } from '../sceneStore'
import { useAuthStore } from '../../../store/authStore'

const STEPS = [
  { key: 'github_connected', label: 'Connect GitHub', panel: 'integrations' },
  { key: 'destination_connected', label: 'Connect Jira or Asana', panel: 'integrations' },
  { key: 'repo_selected', label: 'Select a repository or import an issue', panel: 'context', panelPayload: { mode: 'import' } },
  { key: 'first_draft_reviewed', label: 'Review a draft', panel: 'draft' },
  { key: 'first_published', label: 'Publish a ticket', panel: 'tickets' },
]

export function OnboardingChecklist({ session }) {
  const { isAuthenticated } = useAuthStore()
  const openPanel = useSceneStore((s) => s.openPanel)
  const queryClient = useQueryClient()

  const { data: preferences } = useQuery({
    queryKey: ['preferences'],
    queryFn: async () => {
      const { data } = await api.get('/preferences')
      return data.preferences || {}
    },
    enabled: isAuthenticated,
  })

  const mutation = useMutation({
    mutationFn: (payload) => api.patch('/preferences', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['preferences'] }),
  })

  const onboarding = preferences?.onboarding || {}
  const inferred = useMemo(() => {
    const resources = session?.selected_resources || {}
    return {
      repo_selected: Boolean(resources.github?.repository || resources.github?.repo),
      destination_connected: Boolean(resources.jira || resources.asana),
    }
  }, [session?.selected_resources])

  const completed = {
    ...onboarding,
    ...inferred,
  }

  const remaining = STEPS.filter((step) => !completed[step.key])
  if (!isAuthenticated || remaining.length === 0 || onboarding.dismissed) return null

  const dismiss = () => {
    mutation.mutate({
      onboarding: { ...completed, dismissed: true },
    })
  }

  return (
    <div className="onboarding-checklist" role="region" aria-label="Getting started">
      <div className="onboarding-checklist-hd">
        <span>Getting started</span>
        <button type="button" className="onboarding-dismiss" onClick={dismiss}>Dismiss</button>
      </div>
      <ul className="onboarding-checklist-list">
        {STEPS.map((step) => {
          const done = Boolean(completed[step.key])
          return (
            <li key={step.key} className={done ? 'done' : ''}>
              <span>{done ? '✓' : '○'} {step.label}</span>
              {!done && step.panel && (
                <CrtButton
                  variant="ghost"
                  onClick={() => openPanel(step.panel, step.panelPayload ? { payload: step.panelPayload } : undefined)}
                >
                  Open
                </CrtButton>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
